import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import ErrorHandler from '../../components/ErrorHandler';
import { CreateBoxRes } from '../../types/Box';
import { createBox } from '../Scans';
import { getScanQueryKey } from './useGetScan';

interface UseCreateBoxOptions {
    /** Callback when box is successfully created */
    onSuccess?: (data: CreateBoxRes) => void;
    /** Callback when box creation fails */
    onError?: (error: AxiosError) => void;
    /** Callback when mutation is settled (success or error) */
    onSettled?: () => void;
    /** Whether to show default error toast (default: true) */
    showErrorToast?: boolean;
    /** Whether to invalidate queries (default: true) */
    invalidateQueries?: boolean;
}

export const useCreateBox = (options?: UseCreateBoxOptions) => {
    const queryClient = useQueryClient();

    const { onSuccess, onError, onSettled, showErrorToast = true, invalidateQueries = true } = options || {};

    return useMutation({
        mutationFn: createBox,
        onSuccess: (data: CreateBoxRes) => {
            // Invalidate queries if enabled
            if (invalidateQueries) {
                queryClient.invalidateQueries({ queryKey: getScanQueryKey(data.scanId) });
                queryClient.invalidateQueries({ queryKey: ['scans'] });
            }

            onSuccess?.(data);
        },
        onError: (error: AxiosError) => {
            // Show default error toast if enabled
            if (showErrorToast) {
                ErrorHandler(error);
            }

            onError?.(error);
        },
        onSettled: () => onSettled?.()
    });
};
